import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../auth';
import type { ModelApplicationWithProfile } from '@modelcast/types';
import { authHeaders } from '../../shared/authHeaders';

interface ApplicationsSectionProps {
  roomCode: string;
  onModelSelected: () => void;
}

export function ApplicationsSection({ roomCode, onModelSelected }: ApplicationsSectionProps) {
  const { getToken } = useAuth();
  const [applications, setApplications] = useState<ModelApplicationWithProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  useEffect(() => {
    fetchApplications();
  }, [roomCode]);

  const fetchApplications = async () => {
    try {
      const token = await getToken();
      const response = await fetch(`/api/jobs/rooms/${roomCode}/applications`, {
        headers: authHeaders(token),
      });
      if (response.ok) {
        const data = await response.json();
        setApplications(data.applications);
      } else {
        setError('Failed to load applications');
      }
    } catch (err) {
      console.error('Error fetching applications:', err);
      setError('Failed to load applications');
    } finally {
      setLoading(false);
    }
  };

  const handleSelect = async (applicationId: string) => {
    if (!confirm('Invite this model to your session? You can select more than one model.')) {
      return;
    }

    setActionLoading(applicationId);
    try {
      const token = await getToken();
      const response = await fetch(`/api/jobs/rooms/${roomCode}/applications/${applicationId}/select`, {
        method: 'POST',
        headers: authHeaders(token),
      });
      if (response.ok) {
        await fetchApplications();
        onModelSelected();
      } else {
        const data = await response.json();
        alert(data.error || 'Failed to select model');
      }
    } catch (err) {
      console.error('Error selecting model:', err);
      alert('Failed to select model');
    } finally {
      setActionLoading(null);
    }
  };

  const handleReject = async (applicationId: string) => {
    if (!confirm('Reject this application?')) {
      return;
    }

    setActionLoading(applicationId);
    try {
      const token = await getToken();
      const response = await fetch(`/api/jobs/rooms/${roomCode}/applications/${applicationId}/reject`, {
        method: 'POST',
        headers: authHeaders(token),
      });
      if (response.ok) {
        fetchApplications();
      } else {
        const data = await response.json();
        alert(data.error || 'Failed to reject application');
      }
    } catch (err) {
      console.error('Error rejecting application:', err);
      alert('Failed to reject application');
    } finally {
      setActionLoading(null);
    }
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  const pendingApplications = applications.filter(a => a.status === 'pending');
  const selectedApplication = applications.find(a => a.status === 'selected');
  const rejectedApplications = applications.filter(a => a.status === 'rejected');

  if (loading) {
    return (
      <div className="bg-panel rounded-xl border border-line shadow-sm p-6">
        <p className="text-sm text-text-dim">Loading applications...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-panel rounded-xl border border-line shadow-sm p-6">
        <p className="text-sm text-red-600">{error}</p>
      </div>
    );
  }

  return (
    <div className="bg-panel rounded-xl border border-line shadow-sm overflow-hidden">
      <div className="p-5 border-b border-line">
        <div className="flex items-center justify-between gap-4">
          <div>
            <h2 className="font-display text-lg font-medium text-text">Model Applications</h2>
            <p className="text-sm text-text-dim mt-0.5">
              {pendingApplications.length} pending · {applications.length} total
            </p>
            <p className="text-xs text-text-faint mt-1">
              Selecting a model sends their invitation. You can keep reviewing other applications.
            </p>
          </div>
          <Link
            to="/jobs"
            className="text-sm text-text-dim hover:text-text border-b border-line pb-px hover:border-text"
          >
            View job board
          </Link>
        </div>
      </div>

      {applications.length === 0 ? (
        <div className="p-8 text-center">
          <p className="text-text-dim">No applications yet.</p>
          <p className="text-sm text-text-faint mt-1 italic">
            Models will see your ad on the job board and can apply directly.
          </p>
        </div>
      ) : (
        <div className="divide-y divide-line">
          {/* Selected Model (if any) */}
          {selectedApplication && (
            <div className="p-5 bg-green-50">
              <div className="flex items-start gap-4">
                {/* Photo */}
                {selectedApplication.modelProfile.photos[0] ? (
                  <img
                    src={selectedApplication.modelProfile.photos[0].url}
                    alt={selectedApplication.modelProfile.displayName}
                    className="w-16 h-16 rounded-lg object-cover"
                  />
                ) : (
                  <div className="w-16 h-16 rounded-lg bg-line flex items-center justify-center">
                    <span className="text-xl text-text-faint">?</span>
                  </div>
                )}

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <Link
                      to={`/profile/${selectedApplication.modelProfile.id}`}
                      className="font-medium text-text hover:underline"
                    >
                      {selectedApplication.modelProfile.displayName}
                    </Link>
                    <span className="px-2 py-0.5 bg-green-600 text-white text-xs rounded font-medium">
                      Selected
                    </span>
                  </div>
                  {selectedApplication.modelProfile.country && (
                    <p className="text-sm text-text-dim mt-0.5">
                      {selectedApplication.modelProfile.country}
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Pending Applications */}
          {pendingApplications.map((app) => (
            <div key={app.id} className="p-5">
              <div className="flex items-start gap-4">
                {/* Photo */}
                {app.modelProfile.photos[0] ? (
                  <img
                    src={app.modelProfile.photos[0].url}
                    alt={app.modelProfile.displayName}
                    className="w-16 h-16 rounded-lg object-cover"
                  />
                ) : (
                  <div className="w-16 h-16 rounded-lg bg-line flex items-center justify-center">
                    <span className="text-xl text-text-faint">?</span>
                  </div>
                )}

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <Link
                      to={`/profile/${app.modelProfile.id}`}
                      className="font-medium text-text hover:underline"
                    >
                      {app.modelProfile.displayName}
                    </Link>
                    {app.modelProfile.country && (
                      <span className="text-sm text-text-faint">· {app.modelProfile.country}</span>
                    )}
                  </div>

                  {app.message && (
                    <p className="text-sm text-text-dim mt-1.5 italic line-clamp-2">
                      "{app.message}"
                    </p>
                  )}

                  <p className="text-xs text-text-faint mt-2">
                    Applied {formatDate(app.createdAt)}
                  </p>

                  {/* Actions */}
                  <div className="flex items-center gap-3 mt-3">
                    <button
                      onClick={() => handleSelect(app.id)}
                      disabled={!!actionLoading}
                      className="px-3 py-1.5 bg-text text-bg text-sm font-medium rounded hover:bg-black disabled:opacity-50"
                    >
                      {actionLoading === app.id ? 'Selecting...' : 'Select'}
                    </button>
                    <button
                      onClick={() => handleReject(app.id)}
                      disabled={!!actionLoading}
                      className="px-3 py-1.5 border border-line text-sm text-text-dim hover:text-text hover:border-text rounded disabled:opacity-50"
                    >
                      Reject
                    </button>
                    <Link
                      to={`/profile/${app.modelProfile.id}`}
                      className="text-sm text-text-dim hover:text-text border-b border-line pb-px hover:border-text"
                    >
                      View profile
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          ))}

          {/* Rejected Applications (collapsed) */}
          {rejectedApplications.length > 0 && (
            <details className="group">
              <summary className="p-4 cursor-pointer text-sm text-text-dim hover:text-text list-none flex items-center gap-2">
                <span className="transform transition-transform group-open:rotate-90">▶</span>
                {rejectedApplications.length} rejected application{rejectedApplications.length !== 1 ? 's' : ''}
              </summary>
              <div className="border-t border-line">
                {rejectedApplications.map((app) => (
                  <div key={app.id} className="p-4 flex items-center gap-3 opacity-60">
                    {app.modelProfile.photos[0] ? (
                      <img
                        src={app.modelProfile.photos[0].url}
                        alt={app.modelProfile.displayName}
                        className="w-10 h-10 rounded object-cover grayscale"
                      />
                    ) : (
                      <div className="w-10 h-10 rounded bg-line" />
                    )}
                    <div className="flex-1 min-w-0">
                      <span className="text-sm text-text-dim">
                        {app.modelProfile.displayName}
                      </span>
                      <span className="text-xs text-text-faint ml-2">Rejected</span>
                    </div>
                  </div>
                ))}
              </div>
            </details>
          )}
        </div>
      )}
    </div>
  );
}
